import { useContext, useEffect, useState } from "react";
import { IRegistrationCredentialsFormValues } from "../../../authentication/_interfaces/IRegistrationCredentialsFormValues";
import { Credentials } from "../../../../stores/models/Credentials";
import { RootContext } from "../../../../stores";
import { IAdminInvitationFlowValues } from "../_interfaces/IAdminInvitationFlowValues";
import { AppError } from "../../../../stores/models/errors/AppError";
import { AppErrors } from "../../../../stores/models/constants/AppErrors";

export function useRegistrationCredentialsController(
    values: IRegistrationCredentialsFormValues,
    allValues: IAdminInvitationFlowValues,
    update: (patch: Partial<IRegistrationCredentialsFormValues>) => void,
    onSuccess: (override?: Partial<IRegistrationCredentialsFormValues>) => void
) {
    const rootStore = useContext(RootContext);

    const [credentials] = useState(new Credentials());
    const [error, setError] = useState<string>();
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof IRegistrationCredentialsFormValues, string>>>({});
    const [isSubmitting, setSubmitting] = useState(false);
    const [validationRun, setValidationRun] = useState(false);

    const updateCredentialsObject = (override?: Partial<IRegistrationCredentialsFormValues>) => {
        const effectiveValues = {
            username: values.username || "",
            password: values.password || "",
            confirmedPassword: values.confirmedPassword || "",
            ...override
        };

        credentials.setEmail(effectiveValues.username);
        credentials.setPassword(effectiveValues.password);
        credentials.setConfirmedPassword(effectiveValues.confirmedPassword);
    }

    useEffect(() => {
        updateCredentialsObject();
    }, [values.username, values.password, values.confirmedPassword]);

    useEffect(() => {
        update({ passwordsMatch: credentials.passwordsMatch });
    }, [credentials.passwordsMatch])

    useEffect(() => {
        if (validationRun) {
            setFieldErrors({
                username: credentials.errors.email,
                password: credentials.errors.password,
                confirmedPassword: credentials.errors.confirmedPassword,
                termsOfServiceAccepted: !values.termsOfServiceAccepted ? 'Please review and accept the Terms of Service to continue.' : undefined
            });
        }
    }, [credentials.errors.email, credentials.errors.password, credentials.errors.confirmedPassword, values.termsOfServiceAccepted, validationRun])

    useEffect(() => {
        if (allValues.inviteForCurrentUser) {
            submit();
        }
    }, [allValues.inviteForCurrentUser])

    const isValidSubmission = () => {
        return credentials.validated && values.termsOfServiceAccepted;
    }

    const submit = async (override?: Partial<IRegistrationCredentialsFormValues>) => {
        setSubmitting(true);
        setError(undefined);
        setValidationRun(true);
        credentials.setAllFieldsDirty();

        try {
            updateCredentialsObject(override);
            if (allValues.inviteForCurrentUser || (values.hasAccount || override?.hasAccount)) { // If the flow should move to a different step
                update({ ...override });
                onSuccess(override);
            } else if (isValidSubmission()) {
                const result = await rootStore.invitationStore.acceptAdminInvite(allValues.token, values.termsOfServiceAccepted, values.password);
                const updatedOverride = { ...override, invitationAccepted: true };
                update(updatedOverride);
                onSuccess(updatedOverride);
            }
        } catch (err) {
            if (err instanceof AppError) {
                if (err.code === AppErrors.MATCHING_RECORD_EXISTS.code) {
                    setError("That email is already in use. Try logging in instead.")
                } else {
                    setError(err.message);
                }
            } else {
                setError("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return {
        values,
        onChange: update,
        onSubmit: submit,
        isSubmitting,
        error,
        fieldErrors
    };
}
